import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import React, { createRef, type JSX, type ReactNode } from "react";
import { DirectoryMember, IDMUserTileProps, Member } from "../../../utils/direct-messages";
import { IDthContact } from "../../../dth/clientHttp/models/httpClientModels";
import { MatrixClientPeg } from "../../../MatrixClientPeg";
import { EventType, KnownMembership } from "matrix-js-sdk/src/matrix";
import BaseAvatar from "../avatars/BaseAvatar";
import AccessibleButton, { ButtonEvent } from "../elements/AccessibleButton";
import { _t } from "../../../languageHandler";
import { mediaFromMxc } from "../../../customisations/Media";
import { humanizeTime } from "../../../utils/humanize";
import { SearchResultAvatar } from "../avatars/SearchResultAvatar";
import { CloseIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import { getKeyBindingsManager } from "../../../KeyBindingsManager";
import { KeyBindingAction } from "../../../accessibility/KeyboardShortcuts";
import { buildMemberScores } from "../../../utils/SortMembers";
import DMRoomMap from "../../../utils/DMRoomMap";
import { formatDisplayName, getContactFormatOrDefault } from "../../../dth/utils";
import { ContactStateManager } from "../../../customisations/ContactStateManager";

const logger = baseLogger.getChild("ContactList");

interface IDMRoomTileProps {
    member: Member;
    lastActiveTs?: number;
    email: string;
    onToggle(member: Member): void;
    highlightWord: string;
    isSelected: boolean;
}

class DMRoomTile extends React.PureComponent<IDMRoomTileProps> {
    private onClick = (e: ButtonEvent): void => {
        // Stop the browser from highlighting text
        e.preventDefault();
        e.stopPropagation();

        this.props.onToggle(this.props.member);
    };

    private highlightName(str: string): ReactNode {
        if (!this.props.highlightWord) return str;

        const lowerStr = str.toLowerCase();
        const terms = this.props.highlightWord
            .split(/[\s,]+/)
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean);

        if (terms.length === 0) return str;

        const result: JSX.Element[] = [];
        let i = 0;

        while (i < str.length) {
            let bestIndex = -1;
            let bestTerm = "";

            for (const term of terms) {
                const idx = lowerStr.indexOf(term, i);
                if (idx >= 0 && (bestIndex === -1 || idx < bestIndex)) {
                    bestIndex = idx;
                    bestTerm = term;
                }
            }

            if (bestIndex === -1) {
                result.push(<span key={i + "end"}>{str.substring(i)}</span>);
                break;
            }

            if (bestIndex > i) {
                result.push(<span key={i + "before"}>{str.substring(i, bestIndex)}</span>);
            }

            const match = str.substring(bestIndex, bestIndex + bestTerm.length);
            result.push(
                <span className="mx_InviteDialog_tile--room_highlight" key={bestIndex + "highlight"}>
                    {match}
                </span>,
            );
            i = bestIndex + bestTerm.length;
        }

        return result;
    }

    public render(): React.ReactNode {
        let timestamp: JSX.Element | undefined;
        if (this.props.lastActiveTs) {
            const humanTs = humanizeTime(this.props.lastActiveTs);
            timestamp = <span className="mx_InviteDialog_tile--room_time">{humanTs}</span>;
        }

        const avatarSize = "36px";
        const avatar =
            <BaseAvatar
                url={
                    this.props.member.getMxcAvatarUrl()
                        ? mediaFromMxc(this.props.member.getMxcAvatarUrl()!).getSquareThumbnailHttp(
                            parseInt(avatarSize, 10),
                        )
                        : null
                }
                name={this.props.member.name}
                idName={this.props.member.userId}
                size={avatarSize}
            />


        let checkmark: JSX.Element | undefined;
        if (this.props.isSelected) {
            // To reduce flickering we put the 'selected' room tile above the real avatar
            checkmark = <div className="mx_InviteDialog_tile--room_selected" />;
        }

        // To reduce flickering we put the checkmark on top of the actual avatar (prevents
        // the browser from reloading the image source when the avatar remounts).
        const stackedAvatar = (
            <span className="mx_InviteDialog_tile_avatarStack">
                {avatar}
                {checkmark}
            </span>
        );

        const caption = this.highlightName(this.props.email);

        return (
            <AccessibleButton className="mx_InviteDialog_tile mx_InviteDialog_tile--room" onClick={this.onClick}>
                {stackedAvatar}
                <span className="mx_InviteDialog_tile_nameStack">
                    <div className="mx_InviteDialog_tile_nameStack_name">
                        {this.highlightName(this.props.member.name)}
                    </div>
                    <div className="mx_InviteDialog_tile_nameStack_userId">{caption}</div>
                </span>
                {timestamp}
            </AccessibleButton>
        );
    }
}

class DMUserTile extends React.PureComponent<IDMUserTileProps> {
    private onRemove = (e: ButtonEvent): void => {
        // Stop the browser from highlighting text
        e.preventDefault();
        e.stopPropagation();

        this.props.onRemove!(this.props.member);
    };

    public render(): React.ReactNode {
        const avatarSize = "20px";
        const avatar = <SearchResultAvatar user={this.props.member} size={avatarSize} />;

        let closeButton;
        if (this.props.onRemove) {
            closeButton = (
                <AccessibleButton
                    className="mx_InviteDialog_userTile_remove"
                    onClick={this.onRemove}
                    aria-label={_t("action.remove")}
                >
                    <CloseIcon width="16px" height="16px" />
                </AccessibleButton>
            );
        }

        return (
            <span className="mx_InviteDialog_userTile">
                <span className="mx_InviteDialog_userTile_pill">
                    {avatar}
                    <span className="mx_InviteDialog_userTile_name">{this.props.member.name}</span>
                </span>
                {closeButton}
            </span>
        );
    }
}

interface IProps {
    onTargetsChange?: (targets: Member[]) => void;

    allowMultiple?: boolean; // default true
    showToggle?: boolean; // default true
    showFilter?: boolean; // default true
    roomId?: string;
    busy?: boolean; // default false

}

interface IState {
    contacts: IDthContact[];
    filterText: string;
    targets: Member[];
}

/**
 * Rubrica semplificata con ricerca interna e selezione utenti.
 */
export default class ContactList extends React.PureComponent<IProps, IState> {
    //Aggiungo delle prop di default
    public static defaultProps: Partial<IProps> = {
        allowMultiple: true,
        showFilter: true,
        showToggle: true,
        busy: false
    };
    private editorRef = createRef<HTMLInputElement>();

    public constructor(props: IProps) {
        super(props);
        this.state = {
            contacts: [],
            filterText: "",
            targets: []
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.updateContacts();
    }

    private updateContacts = async (): Promise<void> => {
        try {
            const excluded = this.computeExcludedIds();

            let contacts: IDthContact[] = this.state.contacts;
            // carica i contatti raw dal DTH
            try {
                contacts = await ContactStateManager.getContactsAsync(true);
            } catch (err) {
                logger.error("Errore durante il caricamento dei contatti:", err);
                contacts = [];
            }

            // filtra i contatti che sono in excluded (es. già nella stanza / invitati / bannati)
            const filtered = contacts.filter((c) => !excluded.has(c.matrixName));

            //TODO filtro su term
            this.setState({ contacts: filtered });
        } catch (err) {
            logger.error("Errore durante updateContacts:", err);
            this.setState({ contacts: [] });
        }
    };

    private excludeExternals(homeserver: string, excludedTargetIds: Set<string>): void {
        const client = MatrixClientPeg.safeGet();
        // users with room membership
        const members = Object.values(buildMemberScores(client)).map(({ member }) => member.userId);
        // users with dm membership
        const roomMembers = Object.keys(DMRoomMap.shared().getUniqueRoomsWithIndividuals());
        roomMembers.forEach((id) => members.push(id));
        // filter duplicates and user IDs from external servers
        const externals = new Set(members.filter((id) => !id.includes(homeserver)));
        externals.forEach((id) => excludedTargetIds.add(id));
    }

    private computeExcludedIds(): Set<string> {
        const cli = MatrixClientPeg.safeGet();
        const excludedIds = new Set<string>([cli.getSafeUserId()]);

        if (this.props.roomId) {
            const room = cli.getRoom(this.props.roomId);
            if (room) {
                room.getMembersWithMembership(KnownMembership.Invite).forEach((m) => excludedIds.add(m.userId));
                room.getMembersWithMembership(KnownMembership.Join).forEach((m) => excludedIds.add(m.userId));
                room.getMembersWithMembership(KnownMembership.Ban).forEach((m) => excludedIds.add(m.userId));

                const createEvt = room.currentState.getStateEvents(EventType.RoomCreate, "");
                const isFederated = createEvt?.getContent?.()?.["m.federate"];
                const ourHomeserver = cli.getDomain();
                if (isFederated === false && ourHomeserver) {
                    // riusa la tua funzione esistente per escludere gli esterni
                    this.excludeExternals(ourHomeserver, excludedIds);
                }
            }
        }

        return excludedIds;
    }


    private parseFilter(filter: string): string[] {
        return filter
            .toLowerCase()
            .split(/[\s,;]+/)
            .map((p) => p.trim())
            .filter((p) => !!p);
    }

    private onClickInputArea = (e: React.MouseEvent): void => {
        // Stop the browser from highlighting text
        e.preventDefault();
        e.stopPropagation();

        if (this.editorRef && this.editorRef.current) {
            this.editorRef.current.focus();
        }
    };

    private renderEditor(): JSX.Element | null {
        const targets = this.state.targets.map((t) => (
            <DMUserTile member={t} onRemove={this.props.busy ? undefined : this.removeMember} key={t.userId} />
        ));
        const input = (
            <input
                type="text"
                onKeyDown={this.onKeyDown}
                onChange={this.updateFilter}
                value={this.state.filterText}
                ref={this.editorRef}
                autoFocus={true}
                disabled={
                    this.props.busy//|| (this.props.kind == InviteKind.CallTransfer && this.state.targets.length > 0)
                }
                autoComplete="off"
                placeholder={_t("action.search")}
                data-testid="invite-dialog-input"
            />
        );
        return (
            <div className="mx_InviteDialog_editor" onClick={this.onClickInputArea}>
                {this.props.showToggle === true ? targets : null}
                {input}
            </div>
        );
    }

    private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        // Ora la barra serve solo a cercare, quindi ignoriamo i tasti speciali di invito
        // ma gestiamo ancora il backspace per cancellare eventuali target selezionati
        if (this.props.busy) return;

        const value = e.currentTarget.value.trim();
        const action = getKeyBindingsManager().getAccessibilityAction(e);

        switch (action) {
            case KeyBindingAction.Backspace:
                if (!value && this.state.targets.length > 0 && this.props.allowMultiple !== false) {
                    this.removeMember(this.state.targets[this.state.targets.length - 1]);
                    e.preventDefault();
                }
                break;
            default:
                // nessun comportamento speciale per enter / space
                break;
        }
    };

    private updateFilter = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const term = e.target.value;
        this.setState({ filterText: term });
    };

    private toggleMember = (member: Member): void => {
        if (this.props.busy) return;

        let targets = this.state.targets.map((t) => t); // cheap clone for mutation
        const idx = targets.findIndex((m) => m.userId === member.userId);

        const allowMultiple = this.props.allowMultiple !== false; // default true

        if (allowMultiple) {
            if (idx >= 0) {
                targets.splice(idx, 1);
            } else {
                targets.push(member);
                // when selecting from suggestions, clear filter (same behaviour as before)
                this.setState({ filterText: "" });
            }
        } else {
            // single-select mode: select replaces previous selection.
            if (idx >= 0) {
                // already selected → deselect
                targets = [];
            } else {
                targets = [member];
            }
            // clear filter if any (we're not showing editor usually in DM mode, but safe)
            this.setState({ filterText: "" });
        }

        this.setState({ targets }, () => {
            // notifica il genitore del nuovo elenco targets
            this.props.onTargetsChange?.(this.state.targets);
        });

        if (this.editorRef && this.editorRef.current) {
            this.editorRef.current.focus();
        }
    };


    private removeMember = (member: Member): void => {
        const targets = this.state.targets.map((t) => t); // cheap clone for mutation
        const idx = targets.indexOf(member);
        if (idx >= 0) {
            targets.splice(idx, 1);
            this.setState({ targets }, () => {
                // notifica il genitore
                this.props.onTargetsChange?.(this.state.targets);
            });
        }

        if (this.editorRef && this.editorRef.current) {
            this.editorRef.current.focus();
        }
    };

    public renderContacts(): ReactNode {
        let sourceMembers = this.state.contacts;

        // Gestione del filtro
        if (this.state.filterText.trim()) {
            const terms = this.parseFilter(this.state.filterText).map((t) => t.toLowerCase());
            sourceMembers = sourceMembers.filter((c) => {
                const hay = (
                    (c.firstName || "") +
                    " " +
                    (c.lastName || "") +
                    " " +
                    (c.email || "") +
                    " "
                ).toLowerCase();
                // Ogni parola digitata deve comparire in almeno un campo
                return terms.every((term) => hay.includes(term));
            });

        }
        if (sourceMembers.length === 0) {
            return (<div className="mx_InviteDialog_section">
                <p>{_t("common.no_results")}</p>
            </div>);
        }

        const tiles = sourceMembers.map((c) => (
            <DMRoomTile
                key={c.matrixName}
                member={new DirectoryMember({
                    user_id: c.matrixName,
                    display_name: formatDisplayName(c.firstName, c.lastName, getContactFormatOrDefault()),
                    avatar_url: c.profileImageUrl,
                })}
                email={c.email}
                onToggle={this.toggleMember}
                highlightWord={this.state.filterText}
                isSelected={this.state.targets.some((t) => t.userId === c.matrixName)}
            />
        ));

        return (
            <React.Fragment>
                <div className="mx_InviteDialog_userSections">
                    <div className="mx_InviteDialog_section">
                        {tiles}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    public render(): ReactNode {
        return (
            <React.Fragment>
                {this.props.showFilter === true && <div className="mx_InviteDialog_addressBar">
                    {this.renderEditor()}
                </div>}
                {this.renderContacts()}
            </React.Fragment>
        );
    }
}
